'use client';

import { Building, ChevronsUpDown, LogOut, RotateCcw, User, Users } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import useLogout from '@/modules/common/application/use-logout';
import { FeatureFlag, UserRole } from '@/modules/common/types';

export function NavUser({
  user,
  userRole,
}: {
  user: {
    firstName?: string | null;
    lastName?: string | null;
    email: string;
    avatar: string;
  };
  userRole?: UserRole;
}) {
  const { t } = useTranslation();
  const isExternalUser = userRole === UserRole.EXTERNAL;
  const { isMobile } = useSidebar();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const isMavue2Enabled = useFeatureFlagEnabled(FeatureFlag.MAVUE_2);
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  const fallback =
    `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase() ||
    user.email.charAt(0).toUpperCase() ||
    'U';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={name} />
                <AvatarFallback className="rounded-lg">{fallback}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={name} />
                  <AvatarFallback className="rounded-lg">{fallback}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isMavue2Enabled && (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => navigate(isExternalUser ? '/shareable' : '/')}>
                    <RotateCcw />
                    {t('modules.mavue2.navigation.switchToMavue10')}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate('/app/settings/user')}>
                <User />
                {t('modules.mavue2.navigation.editUserProfile')}
              </DropdownMenuItem>
              {!isExternalUser && (
                <>
                  <DropdownMenuItem onClick={() => navigate('/app/settings/members')}>
                    <Users />
                    {t('modules.mavue2.navigation.inviteMembers')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/app/settings/facilities')}>
                    <Building />
                    {t('modules.mavue2.navigation.createFacility')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logout()}>
              <LogOut />
              {t('modules.mavue2.navigation.logOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
